import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';

export function VendorManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Summary counts
  const pendingCount = vendors.filter(v => 
    v.onboarding_status === 'phase1_submitted' || 
    v.onboarding_status === 'phase2_submitted' || 
    v.onboarding_status === 'draft'
  ).length;
  const approvedCount = vendors.filter(v => 
    v.onboarding_status === 'published'
  ).length;
  const rejectedCount = vendors.filter(v => v.onboarding_status === 'rejected').length;

  useEffect(() => {
    dispatch(updatepageName('vendor_management'));
    fetchVendors();
  }, [dispatch]);

  useEffect(() => {
    filterVendors();
  }, [statusFilter, vendors]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await CustomAxios.get('inventory/view');
      const vendorData = response?.data || [];
      
      // Map the data to include merchant/user information
      const mappedVendors = vendorData.map(vendor => {
        // Extract location and state from pincode address
        const firstPincode = vendor.pincodes?.[0];
        const location = firstPincode?.hub_address || 'N/A';
        // Try to extract state from address (assume last part of comma-separated address is state)
        const addressParts = location !== 'N/A' ? location.split(',').map(s => s.trim()) : [];
        const state = addressParts.length > 1 ? addressParts[addressParts.length - 1] : 'N/A';
        const city = addressParts.length > 2 ? addressParts[addressParts.length - 2] : (addressParts[0] || 'N/A');
        
        // Use mobileno as owner identifier (can be enhanced later to fetch actual merchant name)
        const ownerName = vendor.mobileno ? `Merchant ${vendor.mobileno}` : 'N/A';
        
        return {
          id: vendor.id,
          storeName: vendor.place || 'N/A',
          email: vendor.email || 'N/A',
          ownerName: ownerName,
          phone: vendor.mobileno ? `+91 ${vendor.mobileno}` : 'N/A',
          location: city,
          state: state,
          submittedDate: vendor.created_on ? new Date(vendor.created_on).toISOString().split('T')[0] : 'N/A',
          status: vendor.onboarding_status || 'draft',
          is_approved: vendor.is_approved || false,
          ...vendor
        };
      });
      
      setVendors(mappedVendors);
      setFilteredVendors(mappedVendors);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterVendors = () => {
    let filtered = [...vendors];
    
    if (statusFilter === 'pending') {
      filtered = vendors.filter(v => 
        v.onboarding_status === 'phase1_submitted' || 
        v.onboarding_status === 'phase2_submitted' || 
        v.onboarding_status === 'draft'
      );
    } else if (statusFilter === 'approved') {
      filtered = vendors.filter(v => 
        v.onboarding_status === 'published'
      );
    } else if (statusFilter === 'rejected') {
      filtered = vendors.filter(v => v.onboarding_status === 'rejected');
    }
    
    setFilteredVendors(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    const sorted = [...filteredVendors].sort((a, b) => {
      if (key === 'submittedDate') {
        const dateA = new Date(a.submittedDate);
        const dateB = new Date(b.submittedDate);
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (key === 'storeName') {
        return direction === 'asc' 
          ? a.storeName.localeCompare(b.storeName)
          : b.storeName.localeCompare(a.storeName);
      } else if (key === 'location') {
        return direction === 'asc'
          ? a.location.localeCompare(b.location)
          : b.location.localeCompare(a.location);
      }
      return 0;
    });
    
    setFilteredVendors(sorted);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { label: 'Draft', color: '#FFA500', bgColor: '#FFF4E6' },
      'phase1_submitted': { label: 'Pending', color: '#FFA500', bgColor: '#FFF4E6' },
      'phase2_submitted': { label: 'Pending', color: '#FFA500', bgColor: '#FFF4E6' },
      'phase1_approved': { label: 'Phase 1 Approved', color: '#3B82F6', bgColor: '#DBEAFE' },
      'published': { label: 'Approved', color: '#10B981', bgColor: '#D1FAE5' },
      'rejected': { label: 'Rejected', color: '#EF4444', bgColor: '#FEE2E2' },
    };
    
    const config = statusConfig[status] || statusConfig['draft'];
    
    return (
      <Chip
        label={config.label}
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 500,
          fontSize: '12px',
          height: '24px',
          borderRadius: '12px',
        }}
        icon={status === 'rejected' ? <CancelIcon sx={{ fontSize: '16px', color: config.color }} /> : 
              status === 'published' ? <CheckCircleIcon sx={{ fontSize: '16px', color: config.color }} /> :
              <AccessTimeIcon sx={{ fontSize: '16px', color: config.color }} />}
      />
    );
  };

  const handleViewDetails = (vendorId) => {
    navigate(`/home/add_hub/${vendorId}`);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 0.5, opacity: 0.5 }}>
          <ArrowUpwardIcon sx={{ fontSize: '12px', mb: '-4px' }} />
          <ArrowDownwardIcon sx={{ fontSize: '12px' }} />
        </Box>
      );
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUpwardIcon sx={{ fontSize: '12px', ml: 0.5 }} /> : 
      <ArrowDownwardIcon sx={{ fontSize: '12px', ml: 0.5 }} />;
  };

  return (
    <Box sx={{ padding: '30px', backgroundColor: '#F5F5F5', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/home/dashboard')}
          sx={{ textDecoration: 'none', color: 'text.secondary', cursor: 'pointer' }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Vendor Management</Typography>
      </Breadcrumbs>

      {/* Title */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '36px',
            mb: 1,
          }}
        >
          Vendor Management
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: 'text.secondary',
          }}
        >
          Review and approve vendor applications
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card
          sx={{
            flex: 1,
            minWidth: '200px',
            backgroundColor: '#FFF4E6',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ color: '#FFA500', fontSize: '24px' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#FFA500' }}>
                Pending Review
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#FFA500' }}>
              {pendingCount}
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            minWidth: '200px',
            backgroundColor: '#D1FAE5',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ color: '#10B981', fontSize: '24px' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#10B981' }}>
                Approved
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#10B981' }}>
              {approvedCount}
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            minWidth: '200px',
            backgroundColor: '#FEE2E2',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CancelIcon sx={{ color: '#EF4444', fontSize: '24px' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#EF4444' }}>
                Rejected
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#EF4444' }}>
              {rejectedCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Vendor Table */}
      <Card sx={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600, width: '50px' }}>
                  <Checkbox size="small" />
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => handleSort('storeName')}
                >
                  Store Details
                  <SortIcon columnKey="storeName" />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => handleSort('ownerName')}>
                  Owner
                  <SortIcon columnKey="ownerName" />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => handleSort('location')}>
                  Location
                  <SortIcon columnKey="location" />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => handleSort('submittedDate')}>
                  Submitted
                  <SortIcon columnKey="submittedDate" />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography>Loading vendors...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No vendors found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id} hover>
                    <TableCell>
                      <Checkbox size="small" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                          {vendor.storeName}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                          {vendor.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                          {vendor.ownerName}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                          {vendor.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                          {vendor.location}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                          {vendor.state}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '14px' }}>
                        {vendor.submittedDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(vendor.status)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details" arrow>
                        <Button
                          variant="contained"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDetails(vendor.id)}
                          sx={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '12px',
                            padding: '6px 12px',
                            '&:hover': {
                              backgroundColor: '#059669',
                            },
                          }}
                        >
                          View Details
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

